import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Container } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import WarehouseClientTable from "../components/WarehouseClientTable";
import { useWarehouseClient } from "../../../apis/warehouseClient/WarehouseClientProvider";
import { getPageName } from "../../../locales/translationUtils";
import NavBar, { NavBarItem } from "../../../components/NavBar";
import { GridColDef } from "@mui/x-data-grid";
import AlertOverlay from "../../../components/Alert";
import Paginacao from "../../../types/paginacao";
import { FaEdit } from "react-icons/fa";

const WarehouseClientView: React.FC = () => {
    const { t } = useTranslation();
    const { warehouseClient, fetchWarehouseClientPaginated, apiResponse } = useWarehouseClient();
    const navigate = useNavigate();
    const location = useLocation();
    const [alert, setAlert] = useState<{ message: string; variant: "success" | "danger" | "warning" | "info" } | null>(null);

    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: 20,
        page: 0,
    });

    const [paginacao, setPaginacao] = React.useState({
        pageNum: 0,
        quantityPerPage: 20
    } as Paginacao);
    
    useEffect(() => {
        const newPaginacao = {
            ...paginacao, 
            pageNum: paginationModel.page,
            quantityPerPage: paginationModel.pageSize
        };
        setPaginacao(newPaginacao);
        fetchWarehouseClientPaginated(newPaginacao);
    }, [paginationModel]);

    useEffect(() => {
        if (location.state?.message) {
            setAlert({ message: location.state.message, variant: location.state.variant });

            navigate(location.pathname, { replace: true });

            const timeout = setTimeout(() => setAlert(null), 5000);
            return () => clearTimeout(timeout);
        }
    }, [location, navigate]);

    const navItems: NavBarItem[] = [
        { label: t('warehouseClient.labels.home'), route: '/', iconName: 'house-door' },
        { label: t('warehouseClient.labels.new'), route: '/warehouseclient/create', iconName: 'plus-circle' },
    ];

    const columns: GridColDef[] = [
        {
            field: 'id', 
            headerName: t('warehouseClient.fields.id'), 
            width: 70,
            renderCell: (params) => (
                <span style={{
                    display: "inline-flex",
                    alignItems: "center",
                    color: "black",
                    cursor: "pointer"
                }}
                onClick={() => navigate(`/warehouseClient/${params.row.id}`)}>
                    <FaEdit className="text-primary" style={{ marginRight: "6px", marginLeft: "2px" }}/>
                    {params.row.id}
                </span>
            ),
        },
        {
            field: 'client', 
            headerName: t('warehouseClient.fields.client'), 
            width: 200,
            renderCell: (params) => params.row.client?.name
        },
        {
            field: 'wr', 
            headerName: t('warehouseClient.fields.wr'), 
            width: 200,
            renderCell: (params) => params.row.wr?.name
        },
    ];

    return (
         <Container fluid style={{ height: 'calc(100vh - 200px)', overflow: 'auto' }}>
            <NavBar items={navItems} />
            <div className="mb-3"></div>
            <h2>{getPageName("warehouseClient")}</h2>
            <div className="mb-3"></div>
            <div>
                {alert && (
                    <AlertOverlay
                        message={alert.message}
                        variant={alert.variant}
                        onClose={() => setAlert(null)}
                    />
                )}
            </div>
            <WarehouseClientTable
                rows={warehouseClient || []}
                columns={columns}
                getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even-row" : "odd-row")}
                apiResponse={apiResponse} 
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
            />
        </Container>
    );
};

export default WarehouseClientView;